import React from "react";
import { TIER_COLORS, TEMP_COLORS } from "@/lib/format";

export function PageHeader({ title, subtitle, actions, testid }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-6" data-testid={testid}>
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function StatCard({ label, value, sub, accent, icon: Icon, testid }) {
  return (
    <div className="card-surface p-5 fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
        {Icon && (
          <span className="grid place-items-center h-8 w-8 rounded-md" style={{ background: accent ? `${accent}18` : "#F1F5F9" }}>
            <Icon size={16} style={{ color: accent || "#64748B" }} />
          </span>
        )}
      </div>
      <p className="text-2xl sm:text-3xl font-extrabold font-mono tracking-tight text-slate-900 mt-2">{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

export function SectionCard({ title, children, actions, className = "", testid }) {
  return (
    <div className={`card-surface ${className}`} data-testid={testid}>
      {(title || actions) && (
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          {title && <h3 className="font-semibold text-slate-800">{title}</h3>}
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

export function TierBadge({ tier }) {
  const c = TIER_COLORS[tier] || TIER_COLORS.Silver;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: c.bg, color: c.text, border: `1px solid ${c.ring}` }}>
      {tier}
    </span>
  );
}

export function TempBadge({ temp }) {
  const c = TEMP_COLORS[temp] || TEMP_COLORS.Cold;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
      style={{ background: c.bg, color: c.text }}>
      ● {temp}
    </span>
  );
}

const STATUS_STYLES = {
  verified: { bg: "#ECFDF5", text: "#059669" },
  active: { bg: "#ECFDF5", text: "#059669" },
  approved: { bg: "#ECFDF5", text: "#059669" },
  Won: { bg: "#ECFDF5", text: "#059669" },
  delivered: { bg: "#ECFDF5", text: "#059669" },
  pending_verification: { bg: "#FFFBEB", text: "#D97706" },
  pending: { bg: "#FFFBEB", text: "#D97706" },
  finance_review: { bg: "#EDE9FE", text: "#6D28D9" },
  requested: { bg: "#FFFBEB", text: "#D97706" },
  Registered: { bg: "#EFF6FF", text: "#2563EB" },
  Quoted: { bg: "#EFF6FF", text: "#2563EB" },
  quoted: { bg: "#EFF6FF", text: "#2563EB" },
  ordered: { bg: "#E0E7FF", text: "#4338CA" },
  dispatched: { bg: "#F0FDFA", text: "#0D9488" },
  rejected: { bg: "#FEF2F2", text: "#DC2626" },
  Lost: { bg: "#FEF2F2", text: "#DC2626" },
  reversed: { bg: "#FEF2F2", text: "#DC2626" },
};

export function StatusBadge({ status }) {
  const c = STATUS_STYLES[status] || { bg: "#F1F5F9", text: "#475569" };
  const label = String(status || "").replace(/_/g, " ");
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
      style={{ background: c.bg, color: c.text }}>
      {label}
    </span>
  );
}

export function FutureBadge() {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
      style={{ background: "#EEF2FF", color: "#4338CA", border: "1px solid #C7D2FE" }}>
      Future Integration
    </span>
  );
}

export function Empty({ text = "No records yet." }) {
  return <div className="py-12 text-center text-sm text-slate-400">{text}</div>;
}
